import { Alert, Checkbox, Form, Modal } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import dayjs from 'dayjs';
import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { currencyFormatter } from '~utilities/formatters';
import { IGlobalState } from '~reducer';
import { loanPayoutFigureProcessAction } from '~Loans/actions';
import { loanPayoutFigureSelector, loanSelector } from '~Loans/selectors';
import ILoan from '~Api/Loan/ILoan';
import ILoanPayoutFigure from '~Api/Loan/ILoanPayoutFigure';
import ModalStateEnum from '~UI/ModalStateEnum';
import type { ButtonProps } from 'antd';

export default function ProcessPayoutFigureModal(props: {
    payoutFigureUuid: string;
    isOpen: ModalStateEnum;
    onCancel: () => void;
}): ReactElement {
    const { isOpen, payoutFigureUuid, onCancel } = props;

    const [sendEmail, setSendEmail] = useState<boolean>(false);

    const payoutFigure: ILoanPayoutFigure = useSelector((state: IGlobalState) => loanPayoutFigureSelector(state, payoutFigureUuid));
    const loan: ILoan = useSelector((state: IGlobalState) => loanSelector(state, payoutFigure.loanUuid));

    const dispatch: Dispatch = useDispatch();

    let payoutFigureWarning: string = null;
    if (!!loan.paymentDateNext && dayjs(loan.paymentDateNext) < dayjs(payoutFigure.dischargeDate)) {
        payoutFigureWarning = 'All due payments have not been made for this loan';
    } else if (Math.ceil(loan.amountRemaining) !== Math.ceil(payoutFigure.principalAmount)) {
        payoutFigureWarning = 'Amount remaining for loan does not equal payout figure principal amount';
    }

    const onChangeSendEmail: (event: CheckboxChangeEvent) => void = useCallback((event: CheckboxChangeEvent) => {
        setSendEmail(event.target.checked);
    }, []);

    const onClickProcess: () => void = useCallback(() => {
        dispatch(loanPayoutFigureProcessAction(payoutFigureUuid, sendEmail, !!payoutFigureWarning));
        onCancel();
    }, [
        dispatch,
        onCancel,
        payoutFigureUuid,
        payoutFigureWarning,
        sendEmail,
    ]);

    const warningBlock: JSX.Element = payoutFigureWarning && (
        <Alert className='payout-figure-warning' message={payoutFigureWarning} type='error' />
    );

    const okButtonProps: ButtonProps = {
        danger: payoutFigureWarning !== null,
        type: payoutFigureWarning !== null ? 'default' : 'primary',
    };

    return (
        <Modal
            okButtonProps={okButtonProps}
            okText='Process'
            onCancel={onCancel}
            onOk={onClickProcess}
            open={ModalStateEnum.Open === isOpen}
            title={`Process Payout Figure ${payoutFigure.code}`}
            wrapClassName='loan-process-payout-figure-modal'
        >
            {warningBlock}
            <Form.Item className='payout-figure' label='Payout Figure'>
                {payoutFigure.code}
            </Form.Item>
            <Form.Item className='proposed-payout-date' label='Proposed Payout Date'>
                {dayjs(payoutFigure.dischargeDate).format('DD/MM/YYYY')}
            </Form.Item>
            <Form.Item className='amount' label='Amount'>
                {currencyFormatter.format(payoutFigure.balanceAmount)}
            </Form.Item>
            <Form.Item className='send-email' label='Email Borrower'>
                <Checkbox onChange={onChangeSendEmail} checked={sendEmail} />
            </Form.Item>
        </Modal >
    );
}